/**
 * Утилита для отправки событий в Яндекс.Метрику
 * Используется для отслеживания технических событий и проверки работоспособности элементов сайта
 */

import { YANDEX_METRICA_ID, IS_PRODUCTION } from 'app/config';

export function yaMetrikaInit(surface: 'iframe_widget' | 'main_app') {
  if (YANDEX_METRICA_ID && typeof window !== 'undefined' && window.ym) {
    const counterId = Number(YANDEX_METRICA_ID);

    window.ym(counterId, 'init', {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      webvisor: false,
      trackHash: true,
    });

    window.ym(counterId, 'params', { surface });

    console.log(
      `Yandex.Metrika initialized: ${YANDEX_METRICA_ID}, surface: ${surface}`
    );
  }
}

/**
 * Безопасное получение пути страницы
 */
const getPagePath = (): string => {
  return typeof window !== 'undefined' ? window.location.pathname : '';
};

/**
 * Отправляет событие в Яндекс.Метрику
 * @param eventName - Название события (goal)
 * @param params - Дополнительные параметры события
 */
export const sendMetric = (
  eventName: string,
  params?: Record<string, unknown>
): void => {
  // Отправляем метрики только в браузере, не на этапе пререндера
  if (typeof window === 'undefined') {
    return;
  }

  const counterId = YANDEX_METRICA_ID;

  // Отправляем метрики только в Production окружении
  if (!IS_PRODUCTION || !counterId || !window.ym) {
    // В остальных окружениях только логируем
    // eslint-disable-next-line no-console
    console.log('[Metrics]', eventName, params);
    return;
  }

  try {
    window.ym(Number(counterId), 'reachGoal', eventName, params);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Metrics] Error sending metric:', error);
  }
};

/**
 * Отслеживание клика по кнопке
 */
export const trackButtonClick = (
  buttonName: string,
  additionalParams?: Record<string, unknown>
): void => {
  sendMetric('button_click', {
    button_name: buttonName,
    page: getPagePath(),
    ...additionalParams,
  });
};

/**
 * Отслеживание успешного действия кнопки
 */
export const trackButtonSuccess = (
  buttonName: string,
  actionResult: string,
  durationMs?: number
): void => {
  sendMetric('button_click_success', {
    button_name: buttonName,
    action_result: actionResult,
    duration_ms: durationMs,
    page: getPagePath(),
  });
};

/**
 * Отслеживание ошибки при действии кнопки
 */
export const trackButtonError = (
  buttonName: string,
  errorType: string,
  errorMessage?: string
): void => {
  sendMetric('button_click_error', {
    button_name: buttonName,
    error_type: errorType,
    error_message: errorMessage,
    page: getPagePath(),
  });
};

/**
 * Отслеживание отправки формы
 */
export const trackFormSubmit = (
  formName: string,
  fieldsCount?: number,
  additionalParams?: Record<string, unknown>
): void => {
  sendMetric('form_submit', {
    form_name: formName,
    fields_count: fieldsCount,
    page: getPagePath(),
    ...additionalParams,
  });
};

/**
 * Отслеживание успешной отправки формы
 */
export const trackFormSubmitSuccess = (
  formName: string,
  responseTimeMs?: number,
  statusCode?: number
): void => {
  sendMetric('form_submit_success', {
    form_name: formName,
    response_time_ms: responseTimeMs,
    status_code: statusCode,
    page: getPagePath(),
  });
};

/**
 * Отслеживание ошибки отправки формы
 */
export const trackFormSubmitError = (
  formName: string,
  errorCode?: string,
  errorMessage?: string,
  fieldsWithErrors?: string[]
): void => {
  sendMetric('form_submit_error', {
    form_name: formName,
    error_code: errorCode,
    error_message: errorMessage,
    fields_with_errors: fieldsWithErrors,
    page: getPagePath(),
  });
};

/**
 * Отслеживание ошибки валидации формы
 */
export const trackFormValidationError = (
  formName: string,
  fieldName: string,
  validationRule: string,
  errorMessage?: string
): void => {
  sendMetric('form_validation_error', {
    form_name: formName,
    field_name: fieldName,
    validation_rule: validationRule,
    error_message: errorMessage,
    page: getPagePath(),
  });
};

/**
 * Отслеживание открытия модального окна
 */
export const trackModalOpen = (modalName: string, trigger?: string): void => {
  sendMetric('modal_open', {
    modal_name: modalName,
    trigger,
    page: getPagePath(),
  });
};

/**
 * Отслеживание закрытия модального окна
 */
export const trackModalClose = (
  modalName: string,
  closeMethod: 'click_outside' | 'button' | 'esc',
  durationSeconds?: number
): void => {
  sendMetric('modal_close', {
    modal_name: modalName,
    close_method: closeMethod,
    duration_seconds: durationSeconds,
    page: getPagePath(),
  });
};

/**
 * Отслеживание ошибки модального окна
 */
export const trackModalError = (
  modalName: string,
  errorType: string,
  errorMessage?: string
): void => {
  sendMetric('modal_error', {
    modal_name: modalName,
    error_type: errorType,
    error_message: errorMessage,
    page: getPagePath(),
  });
};

/**
 * Отслеживание API запроса
 */
export const trackApiRequest = (
  endpoint: string,
  method: string,
  requestId?: string
): void => {
  sendMetric('api_request', {
    endpoint,
    method: method.toUpperCase(),
    request_id: requestId,
  });
};

/**
 * Отслеживание успешного API запроса
 */
export const trackApiRequestSuccess = (
  endpoint: string,
  method: string,
  statusCode: number,
  responseTimeMs: number,
  responseSizeBytes?: number
): void => {
  sendMetric('api_request_success', {
    endpoint,
    method: method.toUpperCase(),
    status_code: statusCode,
    response_time_ms: responseTimeMs,
    response_size_bytes: responseSizeBytes,
  });
};

/**
 * Отслеживание ошибки API запроса
 */
export const trackApiRequestError = (
  endpoint: string,
  method: string,
  statusCode?: number,
  errorCode?: string,
  errorMessage?: string,
  retryCount?: number
): void => {
  sendMetric('api_request_error', {
    endpoint,
    method: method.toUpperCase(),
    status_code: statusCode,
    error_code: errorCode,
    error_message: errorMessage,
    retry_count: retryCount,
  });
};

/**
 * Отслеживание таймаута API запроса
 */
export const trackApiRequestTimeout = (
  endpoint: string,
  method: string,
  timeoutMs: number,
  retryCount?: number
): void => {
  sendMetric('api_request_timeout', {
    endpoint,
    method: method.toUpperCase(),
    timeout_ms: timeoutMs,
    retry_count: retryCount,
  });
};

/**
 * Отслеживание JavaScript ошибки
 */
export const trackJsError = (
  errorMessage: string,
  errorType?: string,
  errorStack?: string,
  componentName?: string
): void => {
  sendMetric('js_error', {
    error_message: errorMessage,
    error_type: errorType,
    error_stack: errorStack,
    component_name: componentName,
    page: getPagePath(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  });
};

/**
 * Отслеживание необработанной ошибки
 */
export const trackUnhandledError = (
  errorMessage: string,
  errorType: string,
  componentName?: string
): void => {
  sendMetric('js_error_unhandled', {
    error_message: errorMessage,
    error_type: errorType,
    component_name: componentName,
    page: getPagePath(),
  });
};

/**
 * Отслеживание ошибки React ErrorBoundary
 */
export const trackReactErrorBoundary = (
  errorMessage: string,
  componentName: string,
  errorStack?: string,
  recoveryAction?: string
): void => {
  sendMetric('react_error_boundary', {
    error_message: errorMessage,
    component_name: componentName,
    error_stack: errorStack,
    recovery_action: recoveryAction,
    page: getPagePath(),
  });
};

/**
 * Отслеживание медленной загрузки страницы
 */
export const trackPageLoadSlow = (
  loadTimeMs: number,
  slowResource?: string
): void => {
  sendMetric('page_load_slow', {
    page_path: getPagePath(),
    load_time_ms: loadTimeMs,
    slow_resource: slowResource,
  });
};

/**
 * Отслеживание успешного входа
 */
export const trackAuthLoginSuccess = (
  authMethod: string,
  loginTimeMs?: number
): void => {
  sendMetric('auth_login_success', {
    auth_method: authMethod,
    login_time_ms: loginTimeMs,
    page: getPagePath(),
  });
};

/**
 * Отслеживание ошибки входа
 */
export const trackAuthLoginError = (
  authMethod: string,
  errorCode?: string,
  errorMessage?: string
): void => {
  sendMetric('auth_login_error', {
    auth_method: authMethod,
    error_code: errorCode,
    error_message: errorMessage,
    page: getPagePath(),
  });
};

/**
 * Отслеживание ошибки обновления токена
 */
export const trackTokenRefreshError = (
  errorCode?: string,
  errorMessage?: string,
  retryCount?: number
): void => {
  sendMetric('token_refresh_error', {
    error_code: errorCode,
    error_message: errorMessage,
    retry_count: retryCount,
  });
};

/**
 * Отслеживание ошибки авторизации 401
 */
export const trackAuth401Error = (
  endpoint: string,
  retryAttempted: boolean,
  tokenRefreshed: boolean
): void => {
  sendMetric('auth_401_error', {
    endpoint,
    retry_attempted: retryAttempted,
    token_refreshed: tokenRefreshed,
  });
};

/**
 * Отслеживание показа toast уведомления
 */
export const trackToastShow = (
  toastType: 'success' | 'error' | 'info' | 'warn',
  toastMessage: string,
  durationMs?: number
): void => {
  sendMetric('toast_show', {
    toast_type: toastType,
    toast_message: toastMessage,
    duration_ms: durationMs,
    page: getPagePath(),
  });
};

/**
 * Отслеживание открытия Bottom Sheet
 */
export const trackBottomSheetOpen = (
  sheetName: string,
  trigger?: string
): void => {
  sendMetric('bottom_sheet_open', {
    sheet_name: sheetName,
    trigger,
    page: getPagePath(),
  });
};

/**
 * Отслеживание закрытия Bottom Sheet
 */
export const trackBottomSheetClose = (
  sheetName: string,
  closeMethod: 'swipe' | 'button' | 'backdrop',
  durationSeconds?: number
): void => {
  sendMetric('bottom_sheet_close', {
    sheet_name: sheetName,
    close_method: closeMethod,
    duration_seconds: durationSeconds,
    page: getPagePath(),
  });
};
